// Recurring Invoice — small shared pieces: icons, pills, cells, the data grid,
// the side drawer and the row "more" menu. Display only; the screens own state.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { axHue, axInitials } from "../../adminsitration/ui/adminUi";
import { TYPE_CLS, daysTo, fmtD, money2 } from "./recurringModel";

/* ---------------------------------------------------------------- icons */

const sv = (d, extra = {}) => (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true" {...extra}>
    {d}
  </svg>
);

export const IC = {
  dash: sv(
    <>
      <rect x="1.8" y="1.8" width="5.4" height="5.4" rx="1.2" />
      <rect x="8.8" y="1.8" width="5.4" height="5.4" rx="1.2" />
      <rect x="1.8" y="8.8" width="5.4" height="5.4" rx="1.2" />
      <rect x="8.8" y="8.8" width="5.4" height="5.4" rx="1.2" />
    </>
  ),
  loop: sv(
    <>
      <path d="M14 8a6 6 0 1 1-1.8-4.3M14 2v4h-4" />
      <circle cx="8" cy="8" r="1.4" />
    </>
  ),
  plus: sv(<path d="M8 3.2v9.6M3.2 8h9.6" />, { strokeWidth: 1.9, strokeLinecap: "round" }),
  doc: sv(<path d="M3.6 1.8h6.2l2.6 2.6v9.8H3.6zM9.8 1.8v2.6h2.6M5.8 7.6h4.4M5.8 10.2h3" />),
  chart: sv(<path d="M2 13.6V2.4M2 13.6h12M4.6 11.2V7.6M7.4 11.2V4.6M10.2 11.2V9M13 11.2V6.2" />),
  table: sv(
    <>
      <rect x="2" y="2.4" width="12" height="11.2" rx="1.4" />
      <path d="M2 6h12M6.4 6v7.6" />
    </>
  ),
  gear: sv(
    <>
      <circle cx="8" cy="8" r="2.3" />
      <path d="M8 1.4v1.8M8 12.8v1.8M14.6 8h-1.8M3.2 8H1.4M12.7 3.3l-1.3 1.3M4.6 11.4l-1.3 1.3M12.7 12.7l-1.3-1.3M4.6 4.6 3.3 3.3" />
    </>
  ),
  refresh: sv(<path d="M14 8a6 6 0 1 1-1.8-4.3M14 2v4h-4" />),
  print: sv(<path d="M4.4 6V2.2h7.2V6M4.4 12H3a1.2 1.2 0 0 1-1.2-1.2V7.2A1.2 1.2 0 0 1 3 6h10a1.2 1.2 0 0 1 1.2 1.2v3.6A1.2 1.2 0 0 1 13 12h-1.4M4.4 9.8h7.2v4H4.4z" />),
  play: sv(<path d="M4.4 2.8 12.6 8l-8.2 5.2z" />),
  check: sv(<path d="M3 8.6 6.4 12 13 4.6" />, { strokeWidth: 1.8 }),
  back: sv(<path d="M10 3.5 5.5 8l4.5 4.5" />, { strokeWidth: 1.7, strokeLinecap: "round", strokeLinejoin: "round" }),
  view: sv(
    <>
      <path d="M1.6 8S3.8 3.6 8 3.6 14.4 8 14.4 8 12.2 12.4 8 12.4 1.6 8 1.6 8z" />
      <circle cx="8" cy="8" r="1.9" />
    </>
  ),
  edit: sv(<path d="M11.2 2.4 13.6 4.8 5.6 12.8 2.6 13.4l.6-3z" />),
  del: sv(<path d="M3 4.4h10M6.2 4.4V3h3.6v1.4M4.6 4.4l.6 8.6h5.6l.6-8.6" />),
  cash: sv(
    <>
      <rect x="1.6" y="3.8" width="12.8" height="8.4" rx="1.4" />
      <circle cx="8" cy="8" r="1.8" />
    </>
  ),
  mail: sv(
    <>
      <rect x="1.8" y="3.4" width="12.4" height="9.2" rx="1.4" />
      <path d="m1.8 4.6 6.2 4.2 6.2-4.2" />
    </>
  ),
  cancel: sv(<path d="M4.4 4.4l7.2 7.2M11.6 4.4l-7.2 7.2" />, { strokeWidth: 1.8 }),
  more: (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="3.4" r="1.3" />
      <circle cx="8" cy="8" r="1.3" />
      <circle cx="8" cy="12.6" r="1.3" />
    </svg>
  ),
  warn: sv(<path d="M8 2.2 14.4 13H1.6zM8 6.4v3M8 11.2h.01" />),
  clock: sv(
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M8 4.6V8l2.4 1.6" />
    </>
  ),
  cal: sv(
    <>
      <rect x="2" y="3.2" width="12" height="10.6" rx="1.4" />
      <path d="M5 1.6v3M11 1.6v3M2 6.6h12" />
    </>
  ),
  pause: sv(
    <>
      <rect x="4.4" y="3.4" width="2.6" height="9.2" rx=".8" />
      <rect x="9" y="3.4" width="2.6" height="9.2" rx=".8" />
    </>
  ),
  search: sv(
    <>
      <circle cx="7.2" cy="7.2" r="4.6" />
      <path d="M10.6 10.6 14 14" />
    </>
  ),
  close: sv(<path d="M3 3l10 10M13 3 3 13" />, { strokeWidth: 1.8 }),
  chev: sv(<path d="M6 3.5 10.5 8 6 12.5" />, { strokeWidth: 1.8 }),
  caret: (
    <svg className="axrc-caret" viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3 5l3 3 3-3" />
    </svg>
  ),
};

/* ---------------------------------------------------------------- pills & cells */

export const StatusPill = ({ s }) => (
  <span className={"axrc-pill " + String(s || "").toLowerCase()}>
    <span className="d" />
    {s}
  </span>
);
export const TypeTag = ({ t }) => (t ? <span className={"axrc-tag " + (TYPE_CLS[t] || "")}>{t}</span> : <span className="axrc-dim">—</span>);

export const Avatar = ({ name, big }) => (
  <span className={big ? "axrc-vav" : "axrc-av"} style={{ "--h": axHue(name) }}>
    {axInitials(name)}
  </span>
);

export const CustCell = ({ name, sub }) => (
  <span className="axrc-cust">
    <Avatar name={name} />
    <span className="tx">
      <span className="nm">{name || "—"}</span>
      {sub ? <span className="sb">{sub}</span> : null}
    </span>
  </span>
);

export const DueCell = ({ next }) => {
  if (!next) return <span className="axrc-dim">—</span>;
  const n = daysTo(next);
  const cls = n <= 0 ? "today" : n <= 6 ? "soon" : "later";
  const lbl = n === 0 ? "Today" : n === 1 ? "Tomorrow" : n < 0 ? Math.abs(n) + "d overdue" : "in " + n + "d";
  return (
    <>
      <span className={"axrc-due " + cls}>{fmtD(next)}</span> <span className="axrc-dim small">· {lbl}</span>
    </>
  );
};

/* ---------------------------------------------------------------- buttons */

export const Btn = ({ kind = "", icon, children, className = "", ...rest }) => (
  <button type="button" className={("axrc-btn " + kind + " " + className).trim()} {...rest}>
    {icon}
    {children}
  </button>
);

export const RowBtn = ({ kind = "", title, icon, onClick, disabled }) => (
  <button
    type="button"
    className={"axrc-rowbtn " + kind}
    title={title}
    aria-label={title}
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onClick && onClick(e);
    }}
  >
    {icon}
  </button>
);

export const Seg = ({ options, value, onChange }) => (
  <div className="axrc-seg" role="group">
    {options.map((o) => (
      <button key={o.v} type="button" className={value === o.v ? "on" : ""} onClick={() => onChange(o.v)}>
        {o.l}
      </button>
    ))}
  </div>
);

/* ---------------------------------------------------------------- counters */

export const Counters = ({ items, cols, onPick }) => (
  <div className="axrc-counters" style={cols ? { "--cols": cols } : undefined}>
    {items.map((d) =>
      onPick ? (
        <button key={d.k + d.l} type="button" className={"axrc-ctr " + (d.cls || "") + (d.on ? " on" : "")} onClick={() => onPick(d.k)}>
          <span className="l">{d.l}</span>
          <span className="v">{d.v}</span>
          {d.s ? <span className="s">{d.s}</span> : null}
        </button>
      ) : (
        <div key={d.k + d.l} className={"axrc-ctr static " + (d.cls || "")}>
          <span className="l">{d.l}</span>
          <span className="v">{d.v}</span>
          {d.s ? <span className="s">{d.s}</span> : null}
        </div>
      )
    )}
  </div>
);

/* ---------------------------------------------------------------- filter fields */

export const FSelect = ({ label, value, onChange, options, all, w = "w2", id, on }) => (
  <div className={"axrc-fl " + w + (on || (value && all) ? " on" : "")}>
    <label htmlFor={id}>{label}</label>
    <div className="ctl">
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        {all !== undefined ? <option value="">{all}</option> : null}
        {options.map((o) => (
          <option key={o.v} value={o.v}>
            {o.l}
          </option>
        ))}
      </select>
      {IC.caret}
    </div>
  </div>
);

export const FDate = ({ label, value, onChange, id }) => (
  <div className="axrc-fl w3">
    <label htmlFor={id}>{label}</label>
    <div className="ctl">
      <input type="date" id={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

export const FSearch = ({ value, onChange, placeholder, id }) => (
  <div className="axrc-fl wsearch">
    <label htmlFor={id}>Search</label>
    <div className="axrc-srch">
      {IC.search}
      <input id={id} placeholder={placeholder} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  </div>
);

export const opts = (list) => list.map((x) => (typeof x === "string" ? { v: x, l: x } : x));

/* ---------------------------------------------------------------- data grid */

const BATCH = 40;

// Sortable table, rows drawn 40 at a time as the list scrolls, optional
// grouping with a subtotal per group, and a sticky totals row.
export const DataGrid = ({
  cols,
  rows,
  initialSort,
  rowKey,
  onRowClick,
  group = "",
  groupOf,
  amountOf,
  foot,
  emptyText = "Nothing matches these filters.",
  footBar,
}) => {
  const [sort, setSort] = useState(initialSort || { k: cols[0].k, dir: 1 });
  const [shown, setShown] = useState(BATCH);
  const wrapRef = useRef(null);
  const sentRef = useRef(null);

  const sorted = useMemo(() => {
    const c = cols.find((x) => x.k === sort.k);
    const val = (r) => (c && c.sortv ? c.sortv(r) : r[sort.k]);
    const cmp = (a, b) => {
      const va = val(a);
      const vb = val(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * sort.dir;
      return String(va == null ? "" : va).localeCompare(String(vb == null ? "" : vb)) * sort.dir;
    };
    const out = rows.slice();
    if (group && groupOf) {
      out.sort((a, b) => {
        const ga = groupOf(a, group);
        const gb = groupOf(b, group);
        return ga === gb ? cmp(a, b) : String(ga).localeCompare(String(gb));
      });
    } else out.sort(cmp);
    return out;
  }, [rows, sort, cols, group, groupOf]);

  // a new list starts again from the top
  useEffect(() => {
    setShown(BATCH);
    if (wrapRef.current) wrapRef.current.scrollTop = 0;
  }, [rows, sort, group]);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      setShown(Infinity);
      return undefined;
    }
    const io = new IntersectionObserver(
      (en) => {
        if (en[0].isIntersecting) setShown((s) => s + BATCH);
      },
      { root: wrapRef.current, rootMargin: "260px" }
    );
    if (sentRef.current) io.observe(sentRef.current);
    return () => io.disconnect();
  }, []);

  const visible = sorted.slice(0, shown);
  const groupTotals = useMemo(() => {
    if (!group || !groupOf) return null;
    const m = {};
    sorted.forEach((r) => {
      const g = groupOf(r, group);
      m[g] = m[g] || { n: 0, amt: 0 };
      m[g].n += 1;
      m[g].amt += amountOf ? amountOf(r) : 0;
    });
    return m;
  }, [sorted, group, groupOf, amountOf]);

  const body = [];
  let last = null;
  visible.forEach((r) => {
    if (groupTotals) {
      const g = groupOf(r, group);
      if (g !== last) {
        body.push(
          <tr key={"g-" + g} className="axrc-grow">
            <td colSpan={cols.length}>
              <span className="in">
                <span>{g || "—"}</span>
                <span className="n">{groupTotals[g].n} rows</span>
                <span className="sp" />
                <span className="mono">{money2(groupTotals[g].amt)}</span>
              </span>
            </td>
          </tr>
        );
        last = g;
      }
    }
    body.push(
      <tr key={rowKey(r)} onClick={onRowClick ? () => onRowClick(r) : undefined} className={onRowClick ? "click" : ""}>
        {cols.map((c) => (
          <td key={c.k} className={c.num ? "num" : ""}>
            {c.render ? c.render(r) : r[c.k]}
          </td>
        ))}
      </tr>
    );
  });

  return (
    <div className="axrc-card axrc-tablecard">
      <div className="axrc-tablewrap" ref={wrapRef}>
        <table className="axrc-dt">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.k} className={c.num ? "num" : ""} style={c.w ? { width: c.w } : undefined}>
                  {c.nosort ? (
                    <span className="plain">{c.t}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSort((s) => (s.k === c.k ? { k: c.k, dir: -s.dir } : { k: c.k, dir: 1 }))}
                    >
                      {c.t}
                      {sort.k === c.k ? <span className="arr">{sort.dir > 0 ? "▴" : "▾"}</span> : null}
                    </button>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{body}</tbody>
          {foot && rows.length ? <tfoot>{foot(sorted)}</tfoot> : null}
        </table>
        <div ref={sentRef} style={{ height: 1 }} />
        {!rows.length ? <div className="axrc-nores">{emptyText}</div> : null}
      </div>
      <div className="axrc-tfoot">
        <span>
          Showing <b>{Math.min(shown, sorted.length)}</b> of <b>{sorted.length}</b>
        </span>
        <span className="sp" />
        {footBar}
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- drawer */

export const Drawer = ({ open, title, sub, green, icon, onClose, children, footer }) => {
  const [mounted, setMounted] = useState(open);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(id);
    }
    setShow(false);
    const t = setTimeout(() => setMounted(false), 220);
    return () => clearTimeout(t);
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    const k = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", k);
    return () => document.removeEventListener("keydown", k);
  }, [open, onClose]);
  if (!mounted) return null;
  return (
    <div className="axrc-portal">
      <div className={"axrc-scrim" + (show ? " show" : "")} onClick={onClose} />
      <aside className={"axrc-drawer" + (show ? " show" : "")} aria-label={title} role="dialog">
        <div className="axrc-drhead">
          <div className={"ic" + (green ? " grn" : "")}>{icon || (green ? IC.cash : IC.doc)}</div>
          <div className="tt">
            <h3>{title}</h3>
            {sub ? <div className="s">{sub}</div> : null}
          </div>
          <button type="button" className="axrc-iconbtn" aria-label="Close" onClick={onClose}>
            {IC.close}
          </button>
        </div>
        <div className="axrc-drbody">{children}</div>
        <div className="axrc-drfoot">{footer}</div>
      </aside>
    </div>
  );
};

/* ---------------------------------------------------------------- popover menu */

// items: [{ l, fn, dgr }] or "-" for a separator
export const PopMenu = ({ anchor, items, onClose }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });
  useLayoutEffect(() => {
    if (!anchor) return;
    const r = anchor.getBoundingClientRect();
    const h = ref.current ? ref.current.offsetHeight : 200;
    setPos({
      left: Math.min(window.innerWidth - 226, Math.max(6, r.right - 214)),
      top: r.bottom + 6 + h > window.innerHeight ? Math.max(6, r.top - h - 6) : r.bottom + 6,
    });
  }, [anchor]);
  useEffect(() => {
    const down = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !(anchor && anchor.contains(e.target))) onClose();
    };
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", down);
      document.removeEventListener("keydown", key);
      window.removeEventListener("resize", onClose);
    };
  }, [anchor, onClose]);
  return createPortal(
    <div className="axrc-portal">
      <div className="axrc-pop" ref={ref} style={pos} role="menu">
        {items.map((it, i) =>
          it === "-" ? (
            <div key={i} className="sep" />
          ) : (
            <button
              key={i}
              type="button"
              role="menuitem"
              className={it.dgr ? "dgr" : ""}
              onClick={() => {
                onClose();
                it.fn();
              }}
            >
              {it.l}
            </button>
          )
        )}
      </div>
    </div>,
    document.body
  );
};

/* ---------------------------------------------------------------- misc */

export const SectHead = ({ children, right }) => (
  <div className="axrc-secthead">
    <span>{children}</span>
    {right}
  </div>
);

export const Fact = ({ l, v, s, cls = "" }) => (
  <div className="axrc-fact">
    <div className="l">{l}</div>
    <div className={"v " + cls}>{v}</div>
    {s ? <div className="s">{s}</div> : null}
  </div>
);
